// /assets wire schemas. The detail endpoint also includes the report
// history so the v2 PropiedadDetalleScreen flow has a single round-trip.
//
// Sesión 4 Tanda D enriches the detail response with:
//   - `group` — minimal parent group context (id, name, kind) so
//     `/cartera/:groupId/:assetId` renders the breadcrumb without an
//     extra round-trip.
//   - `milestones` — keyed by reportType, lists the asset's milestone
//     state (pendiente | en_curso | firmada). Today this maps to
//     postventa's `acta_entrega` (Patch S4.11). Recipes that don't
//     declare any milestone reportTypes get an empty map.
//   - `findingsCount` on each report row — Patch S4.17 surfaces this
//     in the history list (`FOL-… · 4 observaciones`).
//
// Reports tagged `is_milestone = true` are filtered OUT of the
// `reports[]` array (Patch S4.21 — milestones live in the milestones
// map, not the history).
import { unitVisitContactSchema } from "./visit-contact.js";
import { assetCreateSchema, assetGroupIdSchema, assetIdSchema, assetSchema, assetUpdateSchema, findingIdSchema, DERIVED_SEVERITIES, emergencyContactSchema, FUENTES_EJE, OBSERVATION_PHASES, OBSERVATION_VISIT_CONFIRMATIONS, OBSERVATION_VISIT_PREFERRED_DAYS_MAX, OBSERVATION_VISIT_SLOTS, observationVisitDateSchema, observationVisitTimeSchema, reportSchema, WARRANTY_ESTADOS, WARRANTY_PARTIDAS, WARRANTY_STARTS, } from "@bitacorapro/core-engine";
import { slugSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { brandSummarySchema } from "./brands.js";
import { ejeFuenteSchema, paginated, taxonomyOptionSchema, taxonomyOptionsSchema, } from "./common.js";
// `falta` tiene UN schema y vive en reports.ts (#0646). Esta arista es
// segura: reports.ts importa sólo brands.js y common.js, así que no
// cierra ningún ciclo con este archivo. Re-tipear `{code,label}` a mano
// acá es literalmente el bug que #0646 corrigió en findings.ts.
import { confianzaFaltaSchema } from "./reports.js";
import { criticalAlertCreateResultSchema } from "./critical-alerts.js";
export const assetParams = z.object({ id: assetIdSchema });
/** GET /assets/:id/observations/:findingId — el detalle por la puerta
 *  asset-scoped. Los dos ids viajan porque los dos se validan: la
 *  observación tiene que colgar de ESE activo, si no el alcance por
 *  activo no protegería nada. */
export const assetObservationDetailParams = z.object({
    id: assetIdSchema,
    findingId: findingIdSchema,
});
export const assetListQuery = z.object({
    assetGroupId: assetGroupIdSchema.optional(),
    search: z.string().trim().min(1).max(255).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().optional(),
});
export const assetCreateBody = assetCreateSchema.omit({ organizationId: true });
export const assetUpdateBody = assetUpdateSchema;
export const assetResponse = assetSchema;
// Wire shape for /assets list items. Extends the engine's assetSchema
// with the optional `latestVisitSeverity` denorm — max severity rank
// across findings of the asset's latest signed report. Null when the
// asset has no signed reports yet. Required by the facility V5 asset
// card on /cartera (ADR-0023 §Consequences). Other verticals receive
// null today; future recipes can opt in.
// `latestVisitAt` carries the signedAt of the same source report so the
// FE can render relative dates ("hoy", "9 may") without an extra round-trip.
export const assetListItemSchema = assetSchema.extend({
    latestVisitSeverity: z.string().nullable().optional(),
    latestVisitAt: z.string().datetime().nullable().optional(),
    // Effective brand for this asset (assets.brand_id ?? asset_groups.brand_id),
    // null when neither is set. Carries the brand logo URL so the FE renders
    // it on the Mis tiendas cards without a follow-up lookup (ADR-0033 §C1).
    brand: brandSummarySchema.nullable().optional(),
});
export const assetListResponse = paginated(assetListItemSchema);
// GET /assets/coverage-summary — org+scope-scoped "Cobertura del mes"
// aggregate (ADR-0049). Decouples the cartera coverage KPI from the
// paginated list: the count is computed over the FULL asset set
// server-side, so it stays correct no matter how the list is windowed.
// `visited` = assets with a signed acta_visita in the current calendar
// month; `period` echoes the month the server computed against (es-CL /
// America-Santiago) so the FE can label "Cobertura de <mes>".
export const assetCoverageSummaryResponse = z.object({
    total: z.number().int().nonnegative(),
    visited: z.number().int().nonnegative(),
    unvisited: z.number().int().nonnegative(),
    period: z.object({
        year: z.number().int(),
        month: z.number().int().min(1).max(12),
    }),
});
// Per-reportType milestone state for the asset detail's milestones map.
// `status` is the engine's normalized lifecycle for milestones:
//   "pendiente" — no row exists yet (the milestone hasn't started)
//   "en_curso"  — a row exists with status != "signed"
//   "firmada"   — the row is signed
// The server derives these from `reports.status`; the FE renders the
// affordance asymmetrically per Patch S4.12 (pendiente → milestone
// card, firmada → ActionRow).
export const assetMilestoneStateSchema = z.object({
    status: z.enum(["pendiente", "en_curso", "firmada"]),
    reportId: z.string().uuid().optional(),
    signedAt: z.string().datetime().optional(),
});
// Per-row history entry — same shape as a Report plus a denormalized
// observaciones count.
// `maxSeverity` (ADR-0023 §Consequences) carries the max severity rank
// across the report's findings, mapped back to a recipe severity key.
// Null when the report has no findings (caller renders the recipe's
// rank-0 "conforme" anchor). Optional for wire-compat; recipes that
// don't surface it (older clients, mock provider) get undefined.
export const assetReportHistoryItemSchema = reportSchema.extend({
    findingsCount: z.number().int().nonnegative(),
    maxSeverity: z.string().nullable().optional(),
});
// ADR-0144 — la ficha de la unidad, proyectada. Viene de
// `assets.attributes` leída con `parseUnitProfile` (engine): una clave
// malformada llega como ausente. Las fechas DOM / CBR no se repiten acá:
// lo que la pantalla necesita de ellas es `garantias`.
export const unitFichaSchema = z.object({
    tipologia: z.string().nullable(),
    estacionamientos: z.array(z.string()),
    bodegas: z.array(z.string()),
    /** Fecha (`YYYY-MM-DD`) o fecha y hora ISO de la entrega material. */
    fechaEntrega: z.string().nullable(),
});
/** Una partida de garantía ya calculada (`computeUnitWarranties`). */
export const unitWarrantySchema = z.object({
    partida: z.enum(WARRANTY_PARTIDAS),
    plazoAnios: z.number().int().positive(),
    desde: z.enum(WARRANTY_STARTS),
    /** Último día cubierto, `YYYY-MM-DD`; null = «Por confirmar». */
    vence: z.string().nullable(),
    estado: z.enum(WARRANTY_ESTADOS),
});
export const unitEmergencyContactSchema = emergencyContactSchema;
/** Bloque presente sólo cuando la receta declara `unitProfile`. */
export const assetUnitProfileSchema = z.object({
    ficha: unitFichaSchema,
    /** Las tres partidas, en orden estructura · instalaciones ·
     *  terminaciones. Vacío cuando la receta no enciende
     *  `legalWarranties`. */
    garantias: z.array(unitWarrantySchema),
    /** Contactos de emergencia del proyecto de la unidad
     *  (`asset_groups.attributes.emergencyContacts`). Vacío = sin acceso. */
    emergencyContacts: z.array(unitEmergencyContactSchema),
});
// Detail = asset + recent reports for that asset (descending by
// createdAt) + optional parent-group context + milestones map.
export const assetDetailResponse = z.object({
    asset: assetSchema,
    reports: z.array(assetReportHistoryItemSchema),
    group: z
        .object({
        id: assetGroupIdSchema,
        name: z.string().min(1),
        kind: slugSchema.optional(),
    })
        .optional(),
    // Effective brand for this asset; null when the vertical doesn't use brands
    // or the asset has none. Carries the brand logo URL (ADR-0033 §C1).
    brand: brandSummarySchema.nullable(),
    // Keyed by `reportType`. Only includes reportTypes the active recipe
    // marks as `milestone: true`. Always present on the response (empty
    // object when the recipe declares no milestones), so the FE can
    // branch on `Object.keys(milestones).length`.
    milestones: z.record(slugSchema, assetMilestoneStateSchema),
    // ADR-0144 — ficha, garantías y contactos de emergencia. Ausente cuando
    // la receta no declara `unitProfile` (arriendos, facility).
    unitProfile: assetUnitProfileSchema.optional(),
    // ADR-0150 (fase 2, corte 7) — el contacto para visitas vigente de la
    // unidad (null = sin contacto). Ausente cuando la receta no tiene canal
    // del propietario (arriendos, facility).
    visitContact: unitVisitContactSchema.nullable().optional(),
});
// GET /assets/:id/observations (ADR-0058) — the per-property
// observation list for verticals with an asset-scoped observation
// lifecycle (broker_postventa's recipe.observationStatuses). Reads
// LIVE findings, never a signed report's frozen snapshot — the whole
// point of this feed is "what's the current state of every
// observation ever raised against this unit," which is exactly the
// data the snapshot deliberately excludes from a signed acta's own
// render. sourceReportType/sourceSignedAt carry the birth-acta's
// provenance so the FE can show "detectado en la Acta de Entrega,
// firmada el 12-03-2026" without a second round-trip. Deliberately
// hand-built (not findingSchema.extend) — a narrower response shape
// than the full finding row, same convention as findingDelegateResponse.
/**
 * El motivo del overlay de cierre (`findings.observation_closed_reason`,
 * CHECK en la base). Un solo vocabulario para todo el wire:
 *
 * - `conformidad`: la contraparte dio Conforme en un hito del hilo.
 * - `auto_approval`: cerrada por falta de respuesta (ADR-0142). La
 *   escribe el job programado de core-api tras dos recordatorios; NUNCA
 *   es una conformidad y se rotula «Cerrada por falta de respuesta». El
 *   propietario la reabre con un toque mientras `reopenableUntil` siga
 *   vigente.
 * - `migrated`: cerrada por la migración delegación → hilo de facility
 *   (ADR-0134 §7). No es una conformidad: nadie la dio en el hilo. Se
 *   lee como cerrada igual que las otras, pero queda identificable para
 *   excluirla de cualquier métrica de conformidad.
 */
export const observationClosedReasonSchema = z.enum(["conformidad", "auto_approval", "migrated"]);
/**
 * F8 (fase 2, corte 6) — la visita agendada del contratista: un día de
 * calendario de America/Santiago («AAAA-MM-DD») y un rango de horas de
 * ese día («HH:MM» de inicio y de término, hora de pared de Santiago,
 * pasos de 30 minutos; corte de ajustes 2026-10-16). La agenda el jefe o el gerente (el carril que reasigna) mientras
 * la observación está con el contratista; se borra al reasignar, al
 * reabrir y donde se borra el plazo del contratista. `providerName` es la
 * EMPRESA (nunca un técnico). Es la primera fecha de la asignación que
 * ve el propietario; el plazo (`dueAt`) sigue siendo interno.
 */
export const observationVisitSchema = z.object({
    date: observationVisitDateSchema,
    startTime: observationVisitTimeSchema,
    endTime: observationVisitTimeSchema,
    providerName: z.string().nullable(),
    /** ADR-0160 — `pending` cuando la propuso el contratista desde su enlace
     *  y nadie la confirmó; null si la agendó el personal. Opcional
     *  (ADR-0021 D6): un core-api anterior no lo manda. Los otros estados
     *  quedan reservados para la próxima ola. */
    confirmation: z.enum(OBSERVATION_VISIT_CONFIRMATIONS).nullable().optional(),
    // ADR-0161 — sólo hacia el propietario y sólo con `pending`; todos
    // opcionales (ADR-0021 D6: un core-api anterior no los manda, y sin
    // `respondable` la PWA no muestra botones).
    /** La propuesta vigente de ESTA observación (para responder). */
    proposalId: z.string().uuid().optional(),
    /** La hora límite del grupo: si no responde antes, se confirma sola.
     *  Null = no se confirma sola (responde antes de la visita). */
    confirmBy: z.string().datetime().nullable().optional(),
    /** ¿Puede responder ahora? (dueño actual, enlace vivo, ventana abierta). */
    respondable: z.boolean().optional(),
    /** Las observaciones del grupo (incluida ésta, la líder primero): una
     *  visita, una respuesta. */
    group: z
        .array(z.object({
        findingId: z.string().uuid(),
        proposalId: z.string().uuid(),
        label: z.string().max(200),
    }))
        .optional(),
});
/** ADR-0161 — lo que el propietario pidió al pedir otro horario. Sólo lo
 *  ve quien respondió (el dueño nuevo de la unidad, no). */
export const observationVisitOtherTimeRequestedSchema = z.object({
    requestedAt: z.string().datetime(),
    preferredDates: z
        .array(observationVisitDateSchema)
        .min(1)
        .max(OBSERVATION_VISIT_PREFERRED_DAYS_MAX),
    preferredSlot: z.enum(OBSERVATION_VISIT_SLOTS),
});
export const assetObservationSchema = z.object({
    id: z.string().uuid(),
    /** Null for a "loose" observation (ADR-0065) — created directly
     *  against the asset via POST /assets/:id/observations, after the
     *  property's milestone acta is already signed. Never belongs to any
     *  report and never appears in any reports.findingsSnapshot. */
    reportId: z.string().uuid().nullable(),
    /** Per-observation reference id (ADR-0059), e.g. "BIT-2026-0047".
     *  Null for verticals whose recipe declares no folioPrefix. */
    folio: z.string().nullable(),
    /** El recinto de la taxonomía postventa (ADR-0129/0122), RESUELTO
     *  server-side contra la `taxonomyVersion` de ESTA fila — nunca la
     *  vigente, y nunca resuelto en el cliente (el catálogo no cruza el
     *  wire: fitness `taxonomy-catalog-never-on-the-wire`).
     *
     *  Llega `null` cuando la fila no tiene `roomCode` (verticales sin
     *  taxonomía, o una observación cuyo eje de recinto todavía está
     *  pendiente) y también cuando su `taxonomyVersion` ya no está en el
     *  registry: misma postura que `opciones` en `GET /findings/:id` — el
     *  cliente no adivina un nombre desde una lista rancia, muestra su
     *  propio fallback. Si la versión SÍ resuelve pero el código no está
     *  en ese catálogo, llega `{codigo, nombre: codigo}` en vez de `null`
     *  — misma disciplina que `resolveCandidateCards`: un código que el
     *  catálogo no conoce se muestra crudo, no se desaparece.
     *
     *  Estrictamente aditivo respecto de `areaName`, que sigue siendo el
     *  centinela `"General"` del intake postventa (`areaCatalogMode:
     *  "none"`) y la clave de agrupación del PDF / de dedup de la cola
     *  offline. El cliente renderiza `recinto?.nombre ?? areaName`. */
    recinto: taxonomyOptionSchema.nullable(),
    /** El NOMBRE del recinto, sin el código. Es la proyección que toda
     *  superficie debe pintar: `recintoNombre ?? areaName`.
     *
     *  Existe porque `recinto` lleva el `codigo` adosado y la regla dura 9
     *  del spec prohíbe que un código de taxonomía llegue al propietario —
     *  y F7 pide que esa supresión sea SERVER-SIDE, no un `{#if}` en el
     *  cliente. Al carril `asset_party` se le manda `recinto: null` y sólo
     *  este campo; a los carriles de operador, los dos. No es una segunda
     *  representación del mismo hecho: es el mismo hecho con el código
     *  quitado, y los dos campos nunca viajan juntos hacia el propietario.
     *
     *  Nulo exactamente cuando `recinto` lo sería (sin `roomCode`, o con
     *  una `taxonomyVersion` que el registry ya no resuelve). */
    recintoNombre: z.string().nullable(),
    areaName: z.string(),
    element: z.string().nullable(),
    category: z.string(),
    severity: z.string(),
    humanDescription: z.string().nullable(),
    /** Model-authored baseline (immutable once written) — distinct from
     *  `humanDescription`, the inspector-edited copy. FindingCard's
     *  "descripción técnica" block. */
    aiDescription: z.string().nullable(),
    /** Suggested remediation (ADR-0058-style AI baseline, immutable) —
     *  populated only when the recipe's analyzeAreaAddendum asks for it
     *  (broker_postventa today). FindingCard's "Recomendación" block. */
    aiRecommendation: z.string().nullable(),
    /** Short semantic headline (ADR-0117), written by the model in the
     *  same analysis call as aiDescription; immutable like it. Null when
     *  the creation path made no analysis call (manual capture, a
     *  checklist row the supervisor overrode) and on rows written before
     *  the column existed — clients fall back to element / a clamped
     *  description. Flows into findingDetailResponse via omit().extend().
     *  `.default(null)`: the dashboard parses findingDetailResponse /
     *  postventaObservationsResponse on ingress (ADR-0021 D6), so a core-api
     *  build that predates the field must still parse. */
    aiTitle: z.string().nullable().default(null),
    /** Raw dictation captured alongside the finding, when present —
     *  FindingCard's "Nota del inspector" callout. Optional at capture
     *  time (photo-only findings have none), so this stays nullable
     *  independent of `aiGenerated`. */
    audioTranscript: z.string().nullable(),
    /** True for AI-classified ad-hoc findings (the hallazgo flow); false
     *  for findings entered through the structured capture flow. Drives
     *  FindingCard's "IA" badge. */
    aiGenerated: z.boolean(),
    /** Photo evidence URLs (ADR-0065) — returned directly on the feed so
     *  the FE never needs a per-report round trip to render thumbnails.
     *  Load-bearing for loose observations specifically: they have no
     *  birth report to fetch evidence from (reportId is null), so this
     *  is their only path to photos. Empty array, never undefined. */
    evidenceUrls: z.array(z.string().url()),
    /** null until the observation-status route (or /delegate) touches
     *  it — a finding with no lifecycle entry yet. */
    observationStatus: z.string().nullable(),
    observationStatusAt: z.string().datetime().nullable(),
    /** Overlay de cierre (ADR-0105 Decisión 4). El cierre es un HITO, no
     *  un sexto estado: cuando esto viene estampado, `observationStatus`
     *  sigue diciendo `resuelta` y la observación está cerrada igual. Todo
     *  lector que pregunte «¿está cerrada?» pasa por `isObservationClosed`
     *  del engine, nunca por `observationClosedAt != null` a secas: falta
     *  el veto de procedencia y la terminalidad por ausencia de aristas.
     *
     *  Las tres columnas existen en la base desde la ola 2 de taxonomía y
     *  hasta acá no cruzaban el wire, así que el badge «Reabierta ×N» del
     *  propietario, la columna «Resolución» del archivo y el divisor entre
     *  ciclos del hilo no tenían de dónde leer. */
    observationClosedAt: z.string().datetime().nullable().optional(),
    observationClosedReason: observationClosedReasonSchema.nullable().optional(),
    /** 1 + reaperturas por no conformidad. El «n_reintentos» del spec se
     *  deriva en lectura (`cycle - 1`), jamás es un segundo contador. */
    observationCycle: z.number().int().positive().nullable().optional(),
    /** ADR-0142 — cuándo se cerraría sola por falta de respuesta, PROYECTADO
     *  por el servidor con las reglas del job (ancla = la entrada en
     *  `resuelta` o la activación del job en el entorno, lo que sea más
     *  tarde; los recordatorios ya enviados corren la fecha si salieron
     *  tarde). Es el inicio del día de cierre en America/Santiago: el
     *  cliente lo formatea como fecha, nunca suma días de la receta. Null
     *  cuando no espera la confirmación del propietario, cuando la receta
     *  no cierra sola o cuando el job no está activo en este entorno.
     *  Opcional (ADR-0021 D6). */
    autoApprovalAt: z.string().datetime().nullable().optional(),
    /** ADR-0142 §4 — último instante (fin del día en America/Santiago) en
     *  que el propietario puede reabrir una observación cerrada por falta
     *  de respuesta. Null si no está cerrada así o la ventana ya pasó: el
     *  botón «Reabrir» sólo existe mientras esto venga. Opcional
     *  (ADR-0021 D6). */
    reopenableUntil: z.string().datetime().nullable().optional(),
    /** ADR-0148 — «N nuevos» en el pie «Ver hilo» de la card del
     *  propietario: hitos del hilo escritos por la inmobiliaria o el
     *  contratista después de SU marca de lectura (`finding_reads`), o de
     *  los últimos 30 días si nunca abrió el hilo. No cuentan los suyos ni
     *  los del sistema. Lo calcula el servidor en una consulta agregada; el
     *  cliente no lo deriva. Sólo viaja hacia la contraparte EXTERNA del
     *  asset; el personal y facility no lo reciben. Opcional (ADR-0021 D6). */
    newEventsCount: z.number().int().nonnegative().optional(),
    /** Reaperturas por no conformidad (`observationCycle - 1`, NULL leído
     *  como ciclo 1). Lo deriva `observationPhase()` del engine —el único
     *  lugar que compara `observationCycle` contra un número— y el cliente
     *  lo pinta tal cual en el modificador «Reabierta ×N», en CUALQUIER
     *  estado (no sólo en triage, a diferencia de `reopened`). Opcional
     *  (ADR-0021 D6): ausente contra un core-api anterior, y el cliente lo
     *  lee como 0. Heredado por `postventaObservationSchema` y
     *  `findingDetailResponse`. */
    reopenCount: z.number().int().nonnegative().optional(),
    /** Dónde está la fila en el ciclo de vida, derivado por
     *  `observationPhase()` del engine en el servidor — el cliente no
     *  re-deriva la fase cuando viene en el wire. Opcional por ADR-0021 D6:
     *  ausente contra un core-api anterior, y entonces el cliente cae a su
     *  derivación local. `postventaObservationSchema` y
     *  `findingDetailResponse` lo re-declaran `.nullable()` en su extend. */
    observationPhase: z.enum(OBSERVATION_PHASES).optional(),
    /** ADR-0141 — la fila es una apelación del rechazo en curso
     *  (`observationPhase().appealed` del engine, modificador derivado como
     *  `reopened`). Opcional (ADR-0021 D6): ausente contra un core-api
     *  anterior, y el cliente lo re-deriva o lo lee como false. */
    appealed: z.boolean().nullable().optional(),
    /** ADR-0141 — el motivo del rechazo vigente (la nota del hito que dejó
     *  la fila en espera de respuesta del propietario). Sólo viene cuando la
     *  fase es `awaiting_receiver_on_rejection`: es lo que la card del
     *  propietario muestra junto a «Estoy de acuerdo» / «Pedir segunda
     *  revisión». Null/ausente en cualquier otra fase. */
    rejectionNote: z.string().nullable().optional(),
    /** La S-code (S1..S4) que el pill de criticidad PINTA: la derivada de
     *  la clasificación, o el `legacyDisplayAs` de la receta para una fila
     *  anterior a la taxonomía (`effectiveObservationSeverity` del engine).
     *  Nunca se persiste. Opcional por ADR-0021 D6; sin ella el cliente cae
     *  a `LEGACY_SEVERITY_TO_DERIVED[severity]`. */
    effectiveSeverity: z.enum(DERIVED_SEVERITIES).nullable().optional(),
    /** Vencimiento del plazo de la asignación vigente
     *  (`observation_due_at`, ADR-0134 §6). Null antes de asignar, tras
     *  reabrir y en filas asignadas antes de la columna (= «sin plazo»).
     *  Opcional (ADR-0021 D6): un core-api anterior no lo manda. */
    dueAt: z.string().datetime().nullable().optional(),
    /** F8 — la visita agendada (`observationVisitSchema`). Null sin fecha;
     *  ausente contra un core-api anterior (ADR-0021 D6). Sólo la llenan las
     *  recetas con contraparte externa. */
    visit: observationVisitSchema.nullable().optional(),
    /** ADR-0161 — el propietario pidió otro horario para la visita vigente
     *  (que ya no viene en `visit`). Null/ausente en cualquier otro caso. */
    visitOtherTimeRequested: observationVisitOtherTimeRequestedSchema.nullable().optional(),
    createdAt: z.string().datetime(),
    /** Null for a loose observation (ADR-0065) — there is no birth acta. */
    sourceReportType: z.string().nullable(),
    /** null when the birth acta is still unsigned (in_progress), or when
     *  this is a loose observation with no birth acta at all. */
    sourceSignedAt: z.string().datetime().nullable(),
});
export const assetObservationsResponse = z.object({
    observations: z.array(assetObservationSchema),
    /** ADR-0163 — las observaciones de la lista que nacieron de una consulta
     *  de ESTE lector (la pill «Desde tu consulta»). Sólo hacia la
     *  contraparte externa autora; ausente para el resto. */
    fromInquiryIds: z.array(z.string().uuid()).optional(),
});
// POST /assets/:id/observations (ADR-0065) — create a "loose" observation
// directly against an asset, no report involved. Same body shape as the
// report-scoped hallazgo flow (photos already uploaded via
// POST /media/presign-upload, optional client-transcribed note) — aliased
// rather than redeclared so the two entry points structurally cannot drift.
// Gated server-side by assertObservationEntryAllowed (core-engine): when
// the active recipe declares `observationsRequireAssetStatusIn`, the
// asset's `asset_status` must be one of the listed values, or the route
// returns 409 ASSET_NOT_ACTIVATED. Un asset sin estado falla cerrado.
// (Antes el gate exigía un acta firmada y devolvía MILESTONE_REQUIRED;
// ADR-0065 enmendado 2026-09-12 cambió el eje de la pregunta.)
//
// F5 sumó un SEGUNDO brazo a esta ruta — el del propietario, que llega
// con el sobre sellado de /classify. El shape de siempre quedó extraído
// acá para que los dos lo compartan y no puedan derivar; ver la nota
// sobre por qué la unión es por presencia y no `discriminatedUnion`,
// más abajo.
const observationCreateBaseShape = {
    note: z.string().trim().max(4000).optional(),
    // ADR-0111: min(0), not min(1) — a note-only hallazgo (a hazard the
    // supervisor can describe but not safely photograph) is a legitimate
    // submission; `requirePhotoOrNote` below keeps "at least one of photo
    // or note" as the real floor, matching the capture screen's .req band.
    photoUrls: z.array(z.string().url()).max(10),
};
/** El piso de evidencia, compartido por los dos brazos de creación y por
 *  `/classify`: al menos una foto O una nota. Extraído a función para que
 *  el carril del propietario no pueda quedarse con una copia que derive
 *  del original. */
function requirePhotoOrNote(body, ctx) {
    if (body.photoUrls.length === 0 && !(body.note && body.note.length > 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["photoUrls"],
            message: "at least one photo or a note is required",
        });
    }
}
// ── F5 · el carril del propietario (ADR-0127) ───────────────────────────
/** Tope de fotos del carril del propietario. Hasta el 2026-10-08 era 4,
 *  menor que el `.max(10)` del brazo de terreno (captura guiada de F6 con
 *  cuatro slots, costo de inferencia por foto). Esa razón ya no aplica:
 *  en la weekly de producto del 2026-10-08 el founder sacó el límite
 *  rígido y lo igualó al de terreno; el mal uso se vigila con uso real en
 *  vez de prevenirse con un tope.
 *
 *  El MISMO tope rige las dos rutas (`/observations/classify` y el brazo
 *  de propietario de `POST /observations`). Si sólo lo llevara una, el
 *  propietario clasificaría con unas fotos y registraría con otras, y la
 *  observación guardada no se parecería a la que se le propuso. */
export const OWNER_OBSERVATION_MAX_PHOTOS = 10;
const ownerPhotoUrls = z.array(z.string().url()).max(OWNER_OBSERVATION_MAX_PHOTOS);
/** Los dos ejes que el propietario cierra A MANO en la pantalla guiada:
 *  el recinto y la partida — «tipo de reparación» en el copy visible.
 *
 *  Viajan como CÓDIGO y sólo como código, en el sentido exacto de la
 *  enmienda de ADR-0127 al invariante 8: el cliente recibió el par
 *  `{codigo, nombre}` en `ejes` / `opciones`, renderizó `nombre`, y acá
 *  devuelve el `codigo` que el humano eligió. Ningún `codigo` se
 *  renderiza jamás — la prohibición absoluta se mantiene a nivel de UI.
 *
 *  `codEspecialidad` NO viaja, ni de ida ni de vuelta: el catálogo
 *  garantiza que el prefijo de la partida ES el código de su especialidad
 *  (`ELE-03` → `ELE`), así que el servidor la deriva. Pedírsela al cliente
 *  sería aceptar como afirmación algo que el servidor calcula solo — y una
 *  afirmación que además puede contradecir a la partida que viaja al lado.
 *
 *  Los dos opcionales: el propietario puede fijar uno, el otro, los dos o
 *  ninguno, y el servidor resuelve el resto. `.strict()` para que un
 *  `codElemento` o un `codEspecialidad` de más no entre en silencio. */
export const observationFijadosSchema = z
    .object({
    codRecinto: z
        .string()
        .regex(/^RC-\d{2}$/)
        .optional(),
    codPartida: z
        .string()
        .regex(/^[A-Z]{3}-\d{2}$/)
        .optional(),
})
    .strict();
/** Mapa eje → fuente PROPIO del carril del propietario, angosto a los dos
 *  ejes que él cierra.
 *
 *  Deliberadamente NO es `ejeFuenteMapSchema` (findings.ts): ese mapa
 *  deriva sus llaves de `CLASSIFICATION_AXIS_CODES` =
 *  `["recinto","elemento","tipo_falla"]`, que es EL SET DEL GATE de
 *  ADR-0121 — sumarle la partida para poder reusarlo acá cambiaría la
 *  retención de toda fila clasificada de todas las verticales, que es un
 *  precio que este carril no tiene por qué cobrar. Lo que se comparte con
 *  `/classification` es el VOCABULARIO (`ejeFuenteSchema`, derivado de
 *  `EJE_FUENTE_CODES`), no el mapa.
 *
 *  Parcial a propósito, igual que allá: un request declara únicamente los
 *  ejes que llenó. */
export const ownerEjeFuenteSchema = z
    .object({
    recinto: ejeFuenteSchema.optional(),
    tipoReparacion: ejeFuenteSchema.optional(),
})
    .strict();
/** POST /assets/:id/observations/classify — el paso 1 del carril del
 *  propietario: manda evidencia, recibe una propuesta legible y los
 *  pickers para corregirla. No escribe nada; el paso 2 es el brazo de
 *  propietario de `POST /assets/:id/observations`.
 *
 *  Mismo piso de evidencia que el brazo de creación (`requirePhotoOrNote`)
 *  y mismo tope de fotos que el brazo de propietario.
 *
 *  `fijados` permite RE-clasificar tras un pick: el humano cierra un eje
 *  y el servidor vuelve a correr la clasificación para el resto.
 *
 *  El eje fijado NO viaja al modelo: el prompt no lo ve, y core-api
 *  SOBRESCRIBE ese eje con el valor humano después de la corrida,
 *  marcándolo `elegido_humano`. Es deliberado y es lo que hace que «los
 *  ejes cerrados a mano no se re-infieren» sea verdadero por
 *  construcción en vez de una esperanza depositada en el prompt — un
 *  prompt no es una restricción dura, y el historial de #0646/#0647 es
 *  un catálogo de subdeclaración donde el prompt pedía otra cosa.
 *  Además mantiene intacta la invariante de `taxonomy.ts` («los hints
 *  nunca deciden `codEspecialidad`») y deja limpio el único float
 *  `confianza`, que leen el gate y las métricas de F12.
 *
 *  Consecuencia asumida: los ejes NO fijados se re-proponen sin conocer
 *  el que sí lo está, así que el `diagnostico` de un 2º intento puede
 *  quedar en tensión con la partida elegida a mano. F12 mide esa
 *  incoherencia antes de que valga la pena pagar un canal nuevo. */
export const assetObservationClassifyBody = z
    .object({
    note: observationCreateBaseShape.note,
    photoUrls: ownerPhotoUrls,
    fijados: observationFijadosSchema.optional(),
})
    .strict()
    .superRefine(requirePhotoOrNote);
export const assetObservationClassifyResponse = z.object({
    /** El sobre sellado (HMAC) que el cliente echa de vuelta sin tocar en el
     *  brazo de creación. OPACO: el cliente no lo parsea, no lo inspecciona
     *  y no decide nada con su contenido — es la prueba de que el servidor
     *  produjo ESTA propuesta, y es lo que impide que el paso 2 se convierta
     *  en un "escribí lo que yo diga" sobre las columnas de taxonomía. */
    propuesta: z.string().min(1).max(8192),
    /** Cuándo deja de valer el sobre. El cliente no lo verifica —el servidor
     *  lo rechaza igual— pero con esto la pantalla puede avisar antes de
     *  perder el trabajo en vez de fallar al enviar. */
    propuestaExpiraEn: z.string().datetime(),
    /** Lo que el propietario LEE. Ninguno de los dos se arma en el cliente a
     *  partir de códigos: llegan redactados. */
    titulo: z.string().min(1),
    diagnostico: z.string().min(1),
    /** Los cuatro ejes propuestos, RESUELTOS server-side contra la versión
     *  de catálogo de esta corrida. El `codigo` viaja para que el cliente lo
     *  eche de vuelta en `fijados` — jamás para renderizarlo (ADR-0127).
     *  `null` en un eje = el clasificador no lo pudo cerrar; la pantalla
     *  abre el picker correspondiente de `opciones`. */
    ejes: z.object({
        recinto: taxonomyOptionSchema.nullable(),
        tipoReparacion: taxonomyOptionSchema.nullable(),
        elemento: taxonomyOptionSchema.nullable(),
        tipoFalla: taxonomyOptionSchema.nullable(),
    }),
    /** Procedencia VERIFICADA por eje, la misma que ya viaja en
     *  `reclassifyEjesPropuestosSchema.fuenteEjes`: `declarado` cuando las
     *  palabras del humano alcanzan por sí solas para elegir una fila,
     *  `inferido` cuando salió de la foto o de un desempate. Cambia el copy
     *  (ratificar lo propio no es lo mismo que elegir sobre una corazonada
     *  de la máquina), no la mecánica.
     *
     *  Acá va `fuenteEjes` y NO el `confianzaPorEje` que el plan pedía:
     *  `SanitizedTaxonomyProposal` lleva UN SOLO float de confianza, así que
     *  replicarlo por eje sería inventar cuatro medidas donde el productor
     *  tiene una — una mentira en el wire, no un dato.
     *
     *  Tres llaves y no cuatro a propósito: el engine no produce procedencia
     *  para la partida, y agregar `tipoReparacion: null` fijo sería la misma
     *  clase de mentira. Opcional por la misma razón que en `/reclassify`:
     *  en un despliegue parcial llega ausente y el cliente falla CERRADO. */
    fuenteEjes: z
        .object({
        recinto: z.enum(FUENTES_EJE).nullable(),
        elemento: z.enum(FUENTES_EJE).nullable(),
        tipoFalla: z.enum(FUENTES_EJE).nullable(),
    })
        .optional(),
    /** Severidad derivada de los tres booleanos (spec invariante 3). Se
     *  llama igual que en `findingClassificationResponse` y
     *  `findingDetailResponse` a propósito: un segundo nombre para el mismo
     *  concepto en rutas hermanas es deriva, no localización. Derivada del
     *  engine (`DERIVED_SEVERITIES`), no re-tipeada. */
    derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable(),
    /** Lo que el gate de confianza echa en falta, con su etiqueta ya
     *  resuelta server-side. Es `confianzaFaltaSchema` de reports.ts, el
     *  ÚNICO schema de `falta` — re-tipear `{code,label}` a mano acá es
     *  literalmente el bug que #0646 corrigió. Vacío = nada pendiente. */
    faltan: z.array(confianzaFaltaSchema),
    /** El catálogo RESUELTO para los pickers, a la versión de esta corrida.
     *  `null` cuando no hay versión resoluble: la pantalla muestra los
     *  pickers no disponibles en vez de adivinar desde una lista rancia,
     *  misma postura que `findingDetailResponse.opciones`.
     *
     *  Los recintos de ámbito común (pasillos, hall, estacionamientos) NO
     *  vienen — un propietario no reporta sobre espacio común por esta vía.
     *  El filtro vive en core-api: el wire no conoce `ambito` y no debería.
     *
     *  Queda fuera de la regla `candidatos*` de fitness
     *  taxonomy-catalog-never-on-the-wire y correctamente: no es un ranking
     *  acotado por observación sino catálogo resuelto, que es justamente lo
     *  que el #0641 del script declara deliberado. */
    opciones: taxonomyOptionsSchema.nullable(),
    /** Las partidas plausibles para ESTA observación, ya resueltas. El
     *  nombre es deliberado: es un ranking acotado por observación, o sea
     *  `candidatos*` en el sentido exacto de fitness
     *  taxonomy-catalog-never-on-the-wire, y hereda su `.max(3)`
     *  ESTRUCTURAL. Renombrarlo para esquivar la guarda dejaría el tope
     *  sostenido por disciplina, que es lo que la guarda existe para
     *  reemplazar. `null` cuando el clasificador no ofreció ninguna. */
    candidatosPartida: z.array(taxonomyOptionSchema).max(3).nullable(),
});
// ── Los dos brazos de POST /assets/:id/observations ─────────────────────
//
// Unión POR PRESENCIA, no `z.discriminatedUnion`, y por dos razones
// independientes que cada una alcanza sola:
//
//   1. El brazo de terreno es un `ZodEffects` (arrastra el `.superRefine`
//      del piso de evidencia), y `z.discriminatedUnion` sólo admite
//      miembros `ZodObject`. No es una preferencia de estilo: no compila.
//   2. Un discriminador literal OBLIGATORIO (`{ via: "owner" | "field" }`)
//      rompería toda PWA ya instalada y, peor, la reproducción de la cola
//      offline (ADR-0120): la cola reproduce bodies ENCOLADOS con la forma
//      vieja, que no tienen el campo y nunca lo van a tener.
//
// La discriminación la hace `.strict()`: un body de terreno no valida
// contra el brazo de propietario (le falta `propuesta`), y un body de
// propietario no valida contra el de terreno (`propuesta` sobra). El
// próximo lector que quiera "arreglar" esto a `discriminatedUnion` tiene
// que romper una de las dos cosas de arriba primero.
/** Byte por byte el body de siempre — el brazo que ya estaba en
 *  producción y que la cola offline reproduce. No se toca. */
export const assetObservationCreateFieldArm = z
    .object(observationCreateBaseShape)
    .strict()
    .superRefine(requirePhotoOrNote);
/** F5 — el paso 2 del carril del propietario: la evidencia otra vez, más
 *  el sobre sellado que `/classify` emitió, los ejes que el humano fijó, y
 *  quién responde por cada uno. `propuesta` es OBLIGATORIA acá: es lo que
 *  distingue este brazo del de terreno, y sin ella el servidor estaría
 *  escribiendo taxonomía que nadie le propuso. */
export const assetObservationCreateOwnerArm = z
    .object({
    ...observationCreateBaseShape,
    photoUrls: ownerPhotoUrls,
    propuesta: z.string().min(1).max(8192),
    fijados: observationFijadosSchema.optional(),
    ejeFuente: ownerEjeFuenteSchema.optional(),
})
    .strict()
    .superRefine(requirePhotoOrNote);
export const assetObservationCreateBody = z.union([
    assetObservationCreateOwnerArm,
    assetObservationCreateFieldArm,
]);
export const assetObservationCreateResponse = z.object({
    observations: z.array(assetObservationSchema),
    /** ADR-0111 — present iff the active recipe declares a
     *  standalone_finding reportType: the born-terminal Report the
     *  observation was anchored to, created in the same transaction.
     *  Absent (not null) on the ADR-0065 loose-observation arm, so shipped
     *  clients parsing the old shape are unaffected. Derived from the
     *  engine's reportSchema rather than hand-rolled (invariant: wire
     *  derives from engine). */
    report: reportSchema.pick({ id: true, reportType: true, folio: true, closedAt: true }).optional(),
    /** ADR-0156 (#0754) — presente sólo cuando alguna de las observaciones
     *  creadas quedó Crítica (S1 efectiva): si el aviso al jefe (o a los
     *  gerentes) se confirmó. Ausente en el resto, así un cliente anterior
     *  no ve nada distinto. */
    criticalAlert: criticalAlertCreateResultSchema.optional(),
});
// --- POST /assets/resolve ----------------------------------------------
// Bulk id → name lookup for the three product entities (brands /
// asset_groups / assets). Used by the dashboard's Equipo screen to
// resolve membership scope ids to human labels per ADR-0022. Tenant-
// scoped: ids belonging to a foreign org return EMPTY results, never
// an error — avoids cross-org existence-leak.
const uuidArray = z.array(z.string().uuid()).max(500);
export const assetsResolveRequestSchema = z
    .object({
    brandIds: uuidArray.optional(),
    assetGroupIds: uuidArray.optional(),
    assetIds: uuidArray.optional(),
})
    .refine((v) => (v.brandIds?.length ?? 0) > 0 ||
    (v.assetGroupIds?.length ?? 0) > 0 ||
    (v.assetIds?.length ?? 0) > 0, { message: "At least one of brandIds, assetGroupIds, assetIds is required" });
export const assetsResolveResponseSchema = z.object({
    brands: z.array(brandSummarySchema),
    assetGroups: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        // Parent brand if any — lets the FE compose "Astra Sport / Zona Norte".
        brandId: z.string().uuid().nullable(),
    })),
    assets: z.array(z.object({
        id: z.string().uuid(),
        // Composed FE-side via `unit ?? address` so the resolve endpoint
        // returns a single human label without exposing two fields.
        name: z.string(),
        assetGroupId: z.string().uuid().nullable(),
    })),
});
// --- GET /assets/assignable ---------------------------------------------
// Flat, unpaginated store picker for the self-service invite forms
// (dashboard /equipo, POST /organizations/me/invitations — ADR-0116).
// Independent of adminOrgAssetSummarySchema (packages/api-types/src/
// admin.ts) even though the shape mirrors it — that schema is scoped to
// the platform-admin-only /admin/orgs/:id/assets surface; this one is a
// generic, any-inviter-eligible-role surface and has no business
// importing from an admin-only file.
export const assignableAssetSummarySchema = z.object({
    id: z.string().uuid(),
    unit: z.string().nullable(),
    address: z.string().nullable(),
    assetGroupId: z.string().uuid().nullable(),
    assetGroupName: z.string().nullable(),
    // True when the store already has a bound supervisor
    // (assets.supervisor_user_id set). Same known gap as
    // adminOrgAssetSummarySchema's hasSupervisor: does not reflect
    // store_manager coverage — a store with a bound jefe but no supervisor
    // still reads as "free" here. The server-side stamp guard in
    // stampInvitationAssets() still catches it correctly at submit time
    // (reported as already_assigned), so the cost is a stale "ya asignada"
    // badge in the picker preview, not a silent mis-invite.
    hasSupervisor: z.boolean(),
    // The store's effective brand — its own, else its zone's, never the
    // org's default (ADR-0165) — so the supervisor invite can offer only the
    // stores of the brand chosen. null when neither level has one. Optional
    // on the wire for older core-api responses.
    brandId: z.string().uuid().nullable().optional(),
});
export const assignableAssetsResponseSchema = z.object({
    assets: z.array(assignableAssetSummarySchema),
});
//# sourceMappingURL=assets.js.map